import type { JSX } from 'react'
import { useId, useMemo } from 'react'
import { drawLength, usePrefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/cn'

/**
 * Спарклайн: форма ряда без осей и подписей, внутри плитки.
 *
 * Спарклайн осознанно БЕЗ hover-слоя: он показывает форму («растёт / просело»),
 * точные значения читаются из числа рядом. Интерактивный слой есть у больших
 * графиков — там он и нужен.
 *
 * Разрывы в данных (null) рвут линию, а не соединяют соседей: «данных нет» и
 * «значение не менялось» — разные факты.
 */
export interface SparklineProps {
  /** Ряд слева направо. `null` — пропуск. */
  points: Array<number | null>
  /** Цвет линии — токен серии (`seriesColor`) или статусный. */
  color: string
  /** Текстовое описание для скринридера: спарклайн — картинка. */
  label: string
  className?: string
  /** Заливка под линией. Для крошечных плиток бывает лишней. */
  fill?: boolean
  height?: number
}

const VIEW_W = 100

export function Sparkline({
  points,
  color,
  label,
  className,
  fill = true,
  height = 32
}: SparklineProps): JSX.Element {
  const reduced = usePrefersReducedMotion()
  const gradientId = useId()

  const geometry = useMemo(() => build(points, height), [points, height])

  if (geometry === null) {
    // Место под будущий график занимаем сразу (иначе плитка подрастёт, когда
    // наберётся вторая точка), но рисуем только базовую линию: залитый
    // прямоугольник читался бы как «сломанный график», а не как «данных ещё нет».
    return (
      <div aria-hidden className={cn('flex items-center', className)} style={{ height }}>
        <span className="h-px w-full bg-border" />
      </div>
    )
  }

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${VIEW_W} ${height}`}
      preserveAspectRatio="none"
      className={cn('w-full overflow-visible', className)}
      style={{ height }}
    >
      {fill ? (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          {geometry.areas.map((area, index) => (
            <path key={index} d={area} fill={`url(#${gradientId})`} />
          ))}
        </>
      ) : null}

      {geometry.lines.map((line, index) => (
        <path
          key={index}
          d={line}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          className={reduced ? undefined : 'anim-draw'}
          style={reduced ? undefined : drawLength(geometry.length)}
        />
      ))}

      {/* Последняя точка: «вот сейчас». Радиус в единицах viewBox исказился бы
          из-за preserveAspectRatio="none", поэтому рисуем её обводкой линии
          нулевой длины — она масштабом не деформируется. */}
      {geometry.last === null ? null : (
        <path
          d={`M ${geometry.last.x} ${geometry.last.y} l 0 0`}
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  )
}

interface Geometry {
  lines: string[]
  areas: string[]
  length: number
  last: { x: number; y: number } | null
}

/**
 * Ряд → пути. Нужно минимум две известные точки: по одной линию не построить,
 * а рисовать «точку в пустоте» в спарклайне бессмысленно.
 */
function build(points: Array<number | null>, height: number): Geometry | null {
  const known = points.filter((value): value is number => value !== null)
  if (points.length < 2 || known.length < 2) return null

  const min = Math.min(...known)
  const max = Math.max(...known)
  // Ровный ряд (min === max) рисуем по центру, а не по нижней кромке.
  const span = max - min
  const pad = 3
  const usable = height - pad * 2

  const xy = (index: number, value: number): { x: number; y: number } => ({
    x: (index / (points.length - 1)) * VIEW_W,
    y: span === 0 ? height / 2 : pad + usable - ((value - min) / span) * usable
  })

  const lines: string[] = []
  const areas: string[] = []
  let current: Array<{ x: number; y: number }> = []
  let last: { x: number; y: number } | null = null

  const flush = (): void => {
    if (current.length >= 2) {
      lines.push(`M ${current.map(point => `${round(point.x)} ${round(point.y)}`).join(' L ')}`)
      const first = current[0]
      const tail = current[current.length - 1]
      if (first !== undefined && tail !== undefined) {
        areas.push(
          `M ${round(first.x)} ${height} L ${current
            .map(point => `${round(point.x)} ${round(point.y)}`)
            .join(' L ')} L ${round(tail.x)} ${height} Z`
        )
      }
    }
    current = []
  }

  points.forEach((value, index) => {
    if (value === null) {
      flush()
      return
    }
    const point = xy(index, value)
    current.push(point)
    last = point
  })
  flush()

  return {
    lines,
    areas,
    // Длина для анимации отрисовки: точная длина пути потребовала бы DOM-замера,
    // а завышенная оценка просто делает первый кадр чуть более «пустым».
    length: VIEW_W * 2,
    last
  }
}

function round(value: number): number {
  return Math.round(value * 100) / 100
}
